import { useEffect, useRef } from 'react';
import { useFormState, Control, FieldErrors } from 'react-hook-form';
import { useNotify } from '../../notification';

/**
 * Collect messages from server-side submission errors (type === 'server').
 * Client validators use type 'manual' / field validators — those stay on the
 * inputs and keep the generic invalid_form toast.
 */
const collectServerErrorMessages = (
    errors: FieldErrors | Record<string, unknown>
): string[] => {
    const messages: string[] = [];
    for (const value of Object.values(errors ?? {})) {
        if (!value || typeof value !== 'object') continue;
        const err = value as {
            type?: string;
            message?: unknown;
            [key: string]: unknown;
        };
        if (err.type === 'server' && typeof err.message === 'string') {
            messages.push(err.message);
            continue;
        }
        // Nested maps: root.serverError, array item fields, etc.
        if (err.type == null) {
            messages.push(
                ...collectServerErrorMessages(
                    err as FieldErrors | Record<string, unknown>
                )
            );
        }
    }
    return messages;
};

/**
 * This hook display an error message on submit in Form and SaveButton.
 *
 * We can't do the form validity check in the form submit handler
 * as the form state may not have been updated yet when onSubmit validation mode is enabled
 * or when the form hasn't been touched at all.
 */
export const useNotifyIsFormInvalid = (
    control?: Control,
    enabled: boolean = true
) => {
    const { submitCount, errors } = useFormState(
        control ? { control } : undefined
    );
    const submitCountRef = useRef(submitCount);
    const notify = useNotify();

    useEffect(() => {
        // Checking the submit count allows us to only display the notification after users
        // tried to submit
        if (submitCount > submitCountRef.current && enabled) {
            submitCountRef.current = submitCount;

            if (Object.keys(errors).length > 0) {
                // PUT/POST validation bodies become type:'server' via setSubmissionErrors
                // (field keys, root string, or root.serverError). Prefer those over the
                // generic invalid_form toast.
                const serverMessages = [
                    ...new Set(collectServerErrorMessages(errors)),
                ];
                notify(
                    serverMessages.length > 0
                        ? serverMessages.join('; ')
                        : 'ra.message.invalid_form',
                    {
                        type: 'error',
                    }
                );
            }
        }
    }, [errors, submitCount, notify, enabled]);
};
